/**
 * The Month Flow chart.
 *
 * Two questions in one picture:
 *   "what lands on which day"  → paired bars, money in above the axis and
 *                                money out below it
 *   "does the account survive" → a balance line on its own axis, with the
 *                                lowest point called out
 *
 * The low point is the whole reason for the line. A month can be comfortably
 * net-positive and still bounce a direct debit on the 10th because everything
 * arrives on the 25th, and a bar chart alone will never show that.
 */

import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { RhythmDay } from '../../types';

interface RhythmChartProps {
  days: RhythmDay[];
  /** Drawn as a dashed rule on the balance axis when set, so "how far above
   *  zero do I need to stay" is visible rather than mental arithmetic. */
  bufferLine?: number | null;
  lowestBalance: number | null;
  lowestBalanceDate: string | null;
  height?: number;
  /** Hide the balance line when the user only cares about the movements. */
  showBalance?: boolean;
}

const fmt = (n: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 2 }).format(n);

const fmtCompact = (n: number) =>
  new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    notation: Math.abs(n) >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: Math.abs(n) >= 10000 ? 1 : 0,
  }).format(n);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-08-13' → '13 Aug'. Built from the string so no timezone can shift it
 *  onto the previous day. */
function shortLabel(day: string): string {
  const [, m, d] = day.split('-');
  return `${Number(d)} ${MONTHS[Number(m) - 1]}`;
}

/** One drawn bar. A day for a month-length range, a week or a month for longer
 *  ones — see `bucketDays`. */
interface Bucket {
  label: string;
  inflow: number;
  outflow: number;
  /** Balance at the CLOSE of the bucket, so the line still reads as "where you
   *  end up" at any point. */
  balance: number;
  /** Every day folded in, for the tooltip. */
  days: RhythmDay[];
}

/**
 * Collapse the day series into something drawable.
 *
 * A year is 365 bars × 3 series: slow to render and impossible to read, with
 * every label overlapping. Past a month the bars become weeks, and past ~15
 * months they become months. Totals are summed and the balance is taken from
 * the last day in each bucket, so the shape of the curve and the size of the
 * bars stay truthful at every zoom level — only the resolution drops.
 */
function bucketDays(days: RhythmDay[]): { buckets: Bucket[]; grain: 'day' | 'week' | 'month' } {
  if (days.length <= 62) {
    return {
      grain: 'day',
      buckets: days.map((d) => ({
        label: shortLabel(d.date),
        inflow: d.inflow,
        outflow: d.outflow,
        balance: d.balance,
        days: [d],
      })),
    };
  }

  const grain: 'week' | 'month' = days.length <= 460 ? 'week' : 'month';
  const groups = new Map<string, RhythmDay[]>();
  const order: string[] = [];

  for (const d of days) {
    let key: string;
    if (grain === 'month') {
      key = d.date.slice(0, 7);
    } else {
      // Week buckets are counted from the range start rather than from a
      // calendar week, so the first bucket is never a stub of two days.
      const offset = Math.floor(
        (new Date(`${d.date}T12:00:00Z`).getTime() - new Date(`${days[0].date}T12:00:00Z`).getTime()) / 86400000
      );
      key = String(Math.floor(offset / 7));
    }
    const existing = groups.get(key);
    if (existing) existing.push(d);
    else {
      groups.set(key, [d]);
      order.push(key);
    }
  }

  const buckets: Bucket[] = order.map((key) => {
    const group = groups.get(key)!;
    const last = group[group.length - 1];
    const first = group[0];
    return {
      label:
        grain === 'month'
          ? `${MONTHS[Number(key.slice(5, 7)) - 1]} ${key.slice(2, 4)}`
          : shortLabel(first.date),
      inflow: group.reduce((s, d) => s + d.inflow, 0),
      outflow: group.reduce((s, d) => s + d.outflow, 0),
      balance: last.balance,
      days: group,
    };
  });

  return { buckets, grain };
}

export function RhythmChart({
  days,
  bufferLine,
  lowestBalance,
  lowestBalanceDate,
  height = 340,
  showBalance = true,
}: RhythmChartProps) {
  const { buckets, grain } = useMemo(() => bucketDays(days), [days]);

  const series = useMemo(() => {
    const built: ApexOptions['series'] = [
      { name: 'Money in', type: 'column', data: buckets.map((b) => round2(b.inflow)) },
      // Negative so outflow hangs below the axis. The tooltip re-absolutes it,
      // so the reader never sees a negative expense.
      { name: 'Money out', type: 'column', data: buckets.map((b) => -round2(b.outflow)) },
    ];
    if (showBalance) {
      built.push({ name: 'Balance', type: 'line', data: buckets.map((b) => round2(b.balance)) });
    }
    return built;
  }, [buckets, showBalance]);

  const options = useMemo<ApexOptions>(() => {
    const categories = buckets.map((b) => b.label);
    // The low point is a DAY, so on a bucketed chart it's marked on whichever
    // bucket contains it — pointing at the right week is honest; inventing a
    // day-precision marker on a weekly axis would not be.
    const lowIndex = lowestBalanceDate
      ? buckets.findIndex((b) => b.days.some((d) => d.date === lowestBalanceDate))
      : -1;

    return {
      chart: {
        type: 'line',
        stacked: false,
        toolbar: { show: false },
        fontFamily: 'inherit',
        animations: { enabled: false }, // the filters retrigger this constantly
        zoom: { enabled: false },
      },
      // Column, column, line — the line needs a visible stroke and the bars
      // none, so widths are per-series.
      stroke: { width: showBalance ? [0, 0, 3] : [0, 0], curve: 'straight' },
      plotOptions: {
        bar: { columnWidth: buckets.length > 40 ? '90%' : '65%', borderRadius: 2 },
      },
      colors: ['#10b981', '#ef4444', '#3b82f6'],
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        // A 31-day month can't show 31 readable labels on a phone; Apex picks
        // a sensible subset when given a tick amount.
        tickAmount: Math.min(buckets.length, 10),
        labels: {
          rotate: -45,
          rotateAlways: false,
          hideOverlappingLabels: true,
          style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: showBalance
        ? [
            {
              seriesName: 'Money in',
              labels: {
                formatter: (v: number) => fmtCompact(Math.abs(v)),
                style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
              },
              title: { text: 'Daily movement', style: { color: 'var(--bs-gray-600)', fontSize: '11px', fontWeight: 500 } },
            },
            // Apex needs one axis object per series; this one is hidden and
            // simply mirrors the first so the two bar series share a scale.
            { seriesName: 'Money in', show: false },
            {
              seriesName: 'Balance',
              opposite: true,
              labels: {
                formatter: (v: number) => fmtCompact(v),
                style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
              },
              title: { text: 'Balance', style: { color: 'var(--bs-gray-600)', fontSize: '11px', fontWeight: 500 } },
            },
          ]
        : [
            {
              seriesName: 'Money in',
              labels: {
                formatter: (v: number) => fmtCompact(Math.abs(v)),
                style: { colors: 'var(--bs-gray-600)', fontSize: '11px' },
              },
            },
            { seriesName: 'Money in', show: false },
          ],
      annotations: {
        yaxis: [
          ...(showBalance && bufferLine !== null && bufferLine !== undefined
            ? [{
                y: bufferLine,
                yAxisIndex: 2,
                borderColor: '#f59e0b',
                strokeDashArray: 4,
                label: {
                  text: `Buffer ${fmtCompact(bufferLine)}`,
                  position: 'left' as const,
                  textAnchor: 'start' as const,
                  style: { background: '#f59e0b', color: '#fff', fontSize: '10px' },
                },
              }]
            : []),
        ],
        points:
          showBalance && lowIndex >= 0 && lowestBalance !== null
            ? [{
                x: categories[lowIndex],
                y: round2(lowestBalance),
                yAxisIndex: 2,
                marker: { size: 6, fillColor: '#fff', strokeColor: lowestBalance < 0 ? '#ef4444' : '#f59e0b', strokeWidth: 3 },
                label: {
                  text: `Low ${fmtCompact(lowestBalance)}`,
                  borderColor: lowestBalance < 0 ? '#ef4444' : '#f59e0b',
                  style: {
                    background: lowestBalance < 0 ? '#ef4444' : '#f59e0b',
                    color: '#fff',
                    fontSize: '10px',
                    padding: { left: 6, right: 6, top: 2, bottom: 2 },
                  },
                },
              }]
            : [],
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'dark',
        custom: ({ dataPointIndex }: any) => {
          const b = buckets[dataPointIndex];
          if (!b) return '';

          // At day resolution the tooltip lists the actual payments. Once
          // buckets span a week or a month that list would run to dozens of
          // rows, so the same payments are rolled up by name — "Groceries ×7"
          // reads better than seven identical lines.
          const all = b.days.flatMap((d) => d.occurrences);
          const rolled = new Map<string, { label: string; direction: 'in' | 'out'; total: number; count: number }>();
          for (const o of all) {
            const key = `${o.label}|${o.direction}`;
            const cur = rolled.get(key) || { label: o.label, direction: o.direction, total: 0, count: 0 };
            cur.total += o.amount;
            cur.count += 1;
            rolled.set(key, cur);
          }
          const sorted = Array.from(rolled.values()).sort((x, y) => y.total - x.total);

          const rows = sorted
            .slice(0, 8)
            .map(
              (o) =>
                `<div class="d-flex justify-content-between gap-3">
                   <span>${escapeHtml(o.label)}${o.count > 1 ? ` <span class="text-muted">×${o.count}</span>` : ''}</span>
                   <span class="${o.direction === 'in' ? 'text-success' : 'text-danger'}">${
                  o.direction === 'in' ? '+' : '−'
                }${fmt(o.total)}</span>
                 </div>`
            )
            .join('');
          const more = sorted.length > 8 ? `<div class="text-muted fs-8">+${sorted.length - 8} more</div>` : '';
          const heading =
            grain === 'day'
              ? b.label
              : `${b.label} — ${shortLabel(b.days[b.days.length - 1].date)}`;
          return `<div class="px-3 py-2" style="min-width:220px">
              <div class="fw-bold mb-1">${heading}</div>
              ${rows || '<div class="text-muted fs-8">Nothing scheduled</div>'}
              ${more}
              <div class="border-top mt-2 pt-1 d-flex justify-content-between gap-3">
                <span class="text-muted">Balance${grain === 'day' ? '' : ' at close'}</span>
                <span class="fw-bold ${b.balance < 0 ? 'text-danger' : ''}">${fmt(b.balance)}</span>
              </div>
            </div>`;
        },
      },
      grid: {
        borderColor: 'var(--bs-gray-200)',
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontSize: '12px',
        markers: { width: 10, height: 10, radius: 10 },
        labels: { colors: 'var(--bs-gray-700)' },
      },
    };
  }, [buckets, grain, bufferLine, lowestBalance, lowestBalanceDate, showBalance]);

  if (days.length === 0) {
    return (
      <div className="text-center text-muted py-10">
        <i className="bi bi-calendar3 fs-2x d-block mb-2 opacity-50" />
        Pick a date range to see the month laid out.
      </div>
    );
  }

  return (
    <>
      <Chart options={options} series={series} type="line" height={height} />
      {grain !== 'day' && (
        // Without this the bars silently change meaning at 63 days and a
        // weekly total reads as a single enormous payment.
        <div className="text-muted fs-8 text-center mt-1">
          <i className="bi bi-info-circle me-1" />
          Each bar is {grain === 'week' ? 'a week' : 'a month'} — the range is too long to draw day by day. The
          day-by-day list below is still exact.
        </div>
      )}
    </>
  );
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** The tooltip is built as an HTML string, so anything user-supplied has to be
 *  escaped — bill names are free text and a stray `<` would break the markup. */
function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
